import { defaultBrpRoadmapConfig } from "@/config/brp-roadmap"
import { getDefaultIcon, getIcon } from "@/helpers/icons"
import { formatNumberToString } from "@/helpers/number-format"

import type { Recommendation, RecommendationGroup } from "@/types/recommendations"

export type RoadmapStrategyOrder = {
    id: string
    stage: string
    duration: number | null
    scenario_applied: string
    ecmKey?: BrpEcmKey | null
    routeKey?: BrpRouteKey | null
    scenarioApplied: {
        name: string
        description: string
    }
    recommendationGroupId?: string | null
}

export type RoadmapStrategy = {
    id: string
    strategyName: string
    strategyDescription: string
    objectiveYear?: number
    objectiveVariable?: string
    stages?: number
    strategy_orders: RoadmapStrategyOrder[]
}

export type PlanMeasure = {
    _key: string
    scenario_applied: string
    name: string
    description: string
    ecmKey?: BrpEcmKey | null
    routeKey?: BrpRouteKey | null
    durationMonths: number | null
    included: boolean
    recommendationGroupId?: string | null
    recommendationIds?: string[]
    comfort?: number
    cost?: string
    icon?: string | null
    defaultIcon?: string | null
}

export type PlanYearStep = {
    _key: string
    stage: number
    year: number | null
    measures: PlanMeasure[]
}

export type BrpRouteKey =
    | "max_comfort"
    | "energy_independents"
    | "reject_fossil_fuels"
    | "boiler_switchers"

export type BrpEcmKey =
    | "roof"
    | "wall_full"
    | "wall_cavity"
    | "windows_doors"
    | "mech_vent"
    | "heat_pump"
    | "pv_8"
    | "pv_16"
    | "heating_controls"

export type BrpRoadmapClusterRule = {
    id: string
    anchorStage: number
    ecmKeys: BrpEcmKey[]
    omitMissing?: boolean
}

export type BrpRoadmapRouteRules = {
    clusters?: BrpRoadmapClusterRule[]
}

export type BrpRoadmapStrategyAvailabilityRule = {
    id: string
    routeKey: BrpRouteKey
    minConstructionYear?: number
}

export type BrpRoadmapConfig = {
    displayRules?: Partial<Record<BrpRouteKey, BrpRoadmapRouteRules>>
    strategyAvailabilityRules?: BrpRoadmapStrategyAvailabilityRule[]
}

export type BrpRoadmapStrategyAvailabilityContext = {
    ageBand?: {
        from?: number | null
        to?: number | null
    } | null
    selectedAge?: number | null
    typologyCode?: string | null
    yearOfConstruction?: string | number | null
}

type StagedPlanMeasure = PlanMeasure & { stage: number }

type PlanConstraintMeasure = {
    ecmKey?: BrpEcmKey | null
    routeKey?: BrpRouteKey | null
}

type PlanConstraintStep<TMeasure extends PlanConstraintMeasure = PlanConstraintMeasure> = {
    measures: TMeasure[]
}

export type PlanMeasureMovePayload = {
    sourceStepIndex: number
    sourceMeasureIndex: number
    targetStepIndex: number
    targetMeasureIndex?: number
}

export type PlanStepMovePayload = {
    sourceStepIndex: number
    targetStepIndex: number
}

function mergeRecommendationIds(left: string[] = [], right: string[] = []) {
    return Array.from(new Set([...left, ...right]))
}

function mergePlanMeasure(target: PlanMeasure, source: PlanMeasure) {
    target.recommendationIds = mergeRecommendationIds(
        target.recommendationIds,
        source.recommendationIds
    )
    target.durationMonths = Math.max(
        target.durationMonths ?? 0,
        source.durationMonths ?? 0
    ) || null
    target.comfort = Math.max(
        target.comfort ?? 0,
        source.comfort ?? 0
    )
}

function normalizeText(value: string | null | undefined) {
    return (value ?? "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
}

function tokenizeTerm(value: string) {
    return value
        .split(" ")
        .map((token) => token.trim())
        .filter((token) => token.length >= 2)
}

function getTokenOverlapScore(left: string, right: string) {
    const rightTokens = new Set(tokenizeTerm(right))

    return tokenizeTerm(left).filter((token) => rightTokens.has(token)).length
}

function getTermMatchScore(left: string, right: string) {
    if (!left || !right) return 0
    if (left === right) return 4
    if (left.includes(right) || right.includes(left)) return 2

    // Fallback for mixed labels like "PV - renewables" vs recommendation names.
    return getTokenOverlapScore(left, right)
}

const GROUP_ALIASES: Record<string, string[]> = {
    Roofs: ["roofs", "roof insulation"],
    Walls: ["walls", "wall insulation"],
    "Windows & Doors": ["windows and doors", "windows doors"],
    "Heating & Controls": ["heating", "boiler", "heat pump"],
    "Ventilation & Air Tightness": ["ventilation", "airtightness"],
    Renewables: ["solar", "pv", "photovoltaic"],
    Others: ["lighting"]
}

const RECOMMENDATION_ALIASES: Record<string, string[]> = {
    "Attic Insulation - 200mm": ["attic insulation", "roof insulation", "attic", "roof"],
    "Flat Roof Insulation": ["flat roof", "flat roof insulation", "warm roof"],
    "Cavity Wall Insulation": ["cavity wall insulation", "cavity wall", "cavity"],
    "External Wall Insulation": ["external wall insulation", "external envelope"],
    "Internal Wall Insulation": ["internal wall insulation", "dry lining"],
    "Windows - Triple Glazed": ["triple glazed", "triple glazing", "windows"],
    "Windows - Double Glazed": ["double glazed", "double glazing", "windows"],
    "Insulated Doors": ["doors", "door", "insulated doors"],
    "Air Source Heat Pump": ["heat pump", "air source heat pump", "hp"],
    "Heating controls only (for boiler)": ["heating controls", "controls", "boiler controls", "heating controls boiler"],
    "New Boiler & Controls": ["boiler and controls", "new boiler controls", "high efficiency boiler heating controls"],
    "New Boiler only": ["new boiler", "boiler only", "high efficiency boiler"],
    "Demand Control Extract Ventilation": ["mechanical ventilation", "demand control extract ventilation", "dcv", "ventilation"],
    "Air tightness package": ["air tightness", "airtightness"],
    "6x400Wp - PV (2.4 kW)": ["pv 6", "pv-6", "2 4 kw", "2.4 kw"],
    "8x400Wp - PV (3.2 kW)": ["pv 8", "pv-8", "3 2 kw", "3.2 kw"],
    "10x400Wp - PV (4 kW)": ["pv 10", "pv-10", "4 kw", "4kw"],
    "16x400Wp - PV (6.4 kW)": ["pv 16", "pv-16", "6 4 kw", "6.4 kw"],
    "Low Energy Lights": ["lighting", "lights", "low energy lights"]
}

const ORDER_REQUIRED_KEYWORDS = [
    "cavity",
    "internal",
    "external",
    "flat",
    "triple",
    "double",
    "boiler",
    "heat pump",
    "mechanical ventilation",
    "pv 8",
    "pv 16"
]

function findRecommendationGroupForOrder(
    order: RoadmapStrategyOrder,
    recommendationGroups: RecommendationGroup[]
) {
    const scenarioName = normalizeText(order.scenarioApplied?.name)
    const scenarioDescription = normalizeText(order.scenarioApplied?.description)

    const matchedGroup = recommendationGroups.find((group) => {
        const aliases = [
            normalizeText(group.name),
            ...(GROUP_ALIASES[group.name] ?? []).map(normalizeText)
        ]

        return aliases.some(
            (alias) =>
                alias === scenarioName ||
                scenarioName.includes(alias) ||
                alias.includes(scenarioName) ||
                (!!scenarioDescription && scenarioDescription.includes(alias))
        )
    })

    if (matchedGroup) return matchedGroup

    const scoredGroups = recommendationGroups.map((group) => ({
        group,
        score: Math.max(
            ...(group.recommendations ?? []).map((recommendation) =>
                getRecommendationMatchScore(order, recommendation)
            ),
            0
        )
    }))
    const bestScore = Math.max(...scoredGroups.map(({ score }) => score), 0)

    return scoredGroups.find(({ score }) => score === bestScore && score > 0)?.group ?? null
}

function getOrderTerms(order: RoadmapStrategyOrder) {
    return [
        normalizeText(order.scenarioApplied?.name),
        normalizeText(order.scenarioApplied?.description),
        normalizeText(order.scenario_applied)
    ].filter(Boolean)
}

function getRecommendationTerms(recommendation: Recommendation) {
    return [
        normalizeText(recommendation.name),
        normalizeText(recommendation.description),
        ...((RECOMMENDATION_ALIASES[recommendation.name] ?? []).map(normalizeText))
    ].filter(Boolean)
}

function includesAny(value: string, candidates: string[]) {
    return candidates.some((candidate) => value.includes(candidate))
}

function getStrategyTerms(strategy: RoadmapStrategy) {
    return [
        normalizeText(strategy.id),
        normalizeText(strategy.strategyName),
        normalizeText(strategy.strategyDescription)
    ].filter(Boolean)
}

function getOrderSearchText(order: RoadmapStrategyOrder) {
    return getOrderTerms(order).join(" ")
}

function getRouteKey(strategy: RoadmapStrategy): BrpRouteKey | null {
    const haystack = getStrategyTerms(strategy).join(" ")

    if (includesAny(haystack, ["max comfort", "maxcomfort", "maximum comfort"])) {
        return "max_comfort"
    }

    if (includesAny(haystack, ["energy independents", "energy independent", "independent"])) {
        return "energy_independents"
    }

    if (includesAny(haystack, ["reject fossil fuels", "reject fossil", "fossil fuels", "fossil"])) {
        return "reject_fossil_fuels"
    }

    if (includesAny(haystack, ["heat pump ready", "boiler switchers", "boiler switcher"])) {
        return "boiler_switchers"
    }

    return null
}

function getEcmKey(order: RoadmapStrategyOrder): BrpEcmKey | null {
    const haystack = getOrderSearchText(order)

    if (includesAny(haystack, ["16 panels", "pv 16", "pv-16", "6 4 kw", "6.4 kw"])) {
        return "pv_16"
    }

    if (includesAny(haystack, ["8 panels", "pv 8", "pv-8", "3 2 kw", "3.2 kw"])) {
        return "pv_8"
    }

    if (includesAny(haystack, ["heating controls", "boiler controls", "controls only"])) {
        return "heating_controls"
    }

    if (includesAny(haystack, ["heat pump", "air source heat pump", " hp "])) {
        return "heat_pump"
    }

    if (includesAny(haystack, ["cavity wall", "cavity"])) {
        return "wall_cavity"
    }

    if (includesAny(haystack, ["wall insulation", "external wall", "internal wall", "wall"])) {
        return "wall_full"
    }

    if (
        includesAny(haystack, [
            "mechanical ventilation",
            "demand control extract ventilation",
            "whole house ventilation",
            "mech vent",
            "ventilation"
        ])
    ) {
        return "mech_vent"
    }

    if (includesAny(haystack, ["windows", "window", "doors", "door", "triple glazed", "double glazed"])) {
        return "windows_doors"
    }

    if (includesAny(haystack, ["roof insulation", "attic insulation", "flat roof", "roof"])) {
        return "roof"
    }

    return null
}

function isGenericGroupMatch(order: RoadmapStrategyOrder, group: RecommendationGroup) {
    const orderTerms = getOrderTerms(order)
    if (getOrderRequiredKeywords(orderTerms).length) return false

    const groupTerms = [
        normalizeText(group.name),
        ...(GROUP_ALIASES[group.name] ?? []).map(normalizeText)
    ]

    return orderTerms.some((orderTerm) =>
        groupTerms.some((groupTerm) => orderTerm === groupTerm)
    )
}

function getRecommendationMatchScore(
    order: RoadmapStrategyOrder,
    recommendation: Recommendation
) {
    const orderTerms = getOrderTerms(order)
    const recommendationTerms = getRecommendationTerms(recommendation)

    const requiredKeywords = getOrderRequiredKeywords(orderTerms)

    if (
        requiredKeywords.length &&
        !requiredKeywords.every((keyword) =>
            recommendationTerms.some((term) => term.includes(keyword))
        )
    ) {
        return 0
    }

    return orderTerms.reduce(
        (total, orderTerm) =>
            total +
            recommendationTerms.reduce(
                (subtotal, recommendationTerm) =>
                    subtotal + getTermMatchScore(orderTerm, recommendationTerm),
                0
            ),
        0
    )
}

function getOrderRequiredKeywords(orderTerms: string[]) {
    return ORDER_REQUIRED_KEYWORDS.filter((keyword) =>
        orderTerms.some((term) => term.includes(keyword))
    )
}

function getRecommendationIdsForOrder(
    order: RoadmapStrategyOrder,
    group: RecommendationGroup | null | undefined
) {
    const recommendations = group?.recommendations ?? []
    const defaultRecommendations = [
        ...recommendations.filter((recommendation) => !recommendation.isExclusive),
        ...recommendations.filter((recommendation) => recommendation.isExclusive).slice(0, 1)
    ]

    if (!group || !recommendations.length) return []

    if (isGenericGroupMatch(order, group)) {
        return defaultRecommendations.map((recommendation) => recommendation.id)
    }

    const scoredRecommendations = recommendations.map((recommendation) => ({
        recommendation,
        score: getRecommendationMatchScore(order, recommendation)
    }))

    const bestScore = Math.max(...scoredRecommendations.map(({ score }) => score), 0)
    if (bestScore <= 0) {
        if (getOrderRequiredKeywords(getOrderTerms(order)).length) return []
        return defaultRecommendations.map((recommendation) => recommendation.id)
    }

    return scoredRecommendations
        .filter(({ score }) => score === bestScore)
        .map(({ recommendation }) => recommendation.id)
}

function getPlanMeasureName(order: RoadmapStrategyOrder, group?: RecommendationGroup | null) {
    return order.scenarioApplied?.name?.trim() || group?.name || ""
}

function getPlanMeasureDescription(
    order: RoadmapStrategyOrder,
    group?: RecommendationGroup | null
) {
    return order.scenarioApplied?.description?.trim() || group?.description || ""
}

function parseConstructionYear(value: string | number | null | undefined) {
    if (typeof value === "number" && Number.isFinite(value)) return value

    const match = String(value ?? "").match(/\b\d{4}\b/)
    return match ? Number(match[0]) : null
}

function isConstructedFromYear(
    context: BrpRoadmapStrategyAvailabilityContext,
    minConstructionYear: number
) {
    const exactYear = parseConstructionYear(context.yearOfConstruction)
    if (exactYear !== null) return exactYear >= minConstructionYear

    const ageBandFrom = context.ageBand?.from
    if (typeof ageBandFrom === "number") return ageBandFrom >= minConstructionYear

    const selectedAge = context.selectedAge
    if (typeof selectedAge === "number") return selectedAge >= minConstructionYear

    return false
}

function isStrategyAvailabilityRuleMet(
    rule: BrpRoadmapStrategyAvailabilityRule,
    context: BrpRoadmapStrategyAvailabilityContext
) {
    if (
        typeof rule.minConstructionYear === "number" &&
        !isConstructedFromYear(context, rule.minConstructionYear)
    ) {
        return false
    }

    return true
}

export function isRoadmapStrategyAvailable(
    strategy: RoadmapStrategy,
    context: BrpRoadmapStrategyAvailabilityContext,
    roadmapConfig: BrpRoadmapConfig = defaultBrpRoadmapConfig
) {
    const routeKey = getRouteKey(strategy)
    const rules = (roadmapConfig.strategyAvailabilityRules ?? [])
        .filter((rule) => routeKey && rule.routeKey === routeKey)

    return rules.every((rule) => isStrategyAvailabilityRuleMet(rule, context))
}

export function filterRoadmapStrategies(
    strategies: RoadmapStrategy[],
    context: BrpRoadmapStrategyAvailabilityContext,
    roadmapConfig: BrpRoadmapConfig = defaultBrpRoadmapConfig
) {
    return strategies.filter((strategy) =>
        isRoadmapStrategyAvailable(strategy, context, roadmapConfig)
    )
}

function applyRoadmapDisplayRules(
    strategy: RoadmapStrategy,
    measures: StagedPlanMeasure[],
    roadmapConfig: BrpRoadmapConfig
) {
    const routeKey = getRouteKey(strategy)
    const clusters = routeKey
        ? roadmapConfig.displayRules?.[routeKey]?.clusters ?? []
        : []

    return clusters.reduce((nextMeasures, cluster) => {
        const clusterEcmKeys = new Set(cluster.ecmKeys)
        const matchedMeasures = nextMeasures.filter(
            (measure) => measure.ecmKey && clusterEcmKeys.has(measure.ecmKey)
        )

        if (!matchedMeasures.length) return nextMeasures

        if (
            !cluster.omitMissing &&
            cluster.ecmKeys.some(
                (ecmKey) => !matchedMeasures.some((measure) => measure.ecmKey === ecmKey)
            )
        ) {
            return nextMeasures
        }

        return nextMeasures.map((measure) =>
            measure.ecmKey && clusterEcmKeys.has(measure.ecmKey)
                ? {
                    ...measure,
                    stage: cluster.anchorStage
                }
                : measure
        )
    }, measures)
}

export function populateRoadmapOrdersWithRecommendationGroups(
    roadmap: RoadmapStrategy[],
    recommendationGroups: RecommendationGroup[]
): RoadmapStrategy[] {
    return roadmap.map((strategy) => ({
        ...strategy,
        strategy_orders: strategy.strategy_orders.map((order) => {
            const matchedGroup = findRecommendationGroupForOrder(
                order,
                recommendationGroups
            )
            const routeKey = getRouteKey(strategy)
            const ecmKey = getEcmKey(order)

            return {
                ...order,
                ecmKey,
                routeKey,
                recommendationGroupId: matchedGroup?.id ?? null
            }
        })
    }))
}

export function buildPlanFromStrategy(
    strategy: RoadmapStrategy,
    recommendationGroups: RecommendationGroup[],
    startYear: number,
    maxAllowedYear: number,
    roadmapConfig: BrpRoadmapConfig = defaultBrpRoadmapConfig
): PlanYearStep[] {
    const measures: StagedPlanMeasure[] = []
    const usedRecommendationIdsByGroup = new Map<string, Set<string>>()

    for (const order of strategy.strategy_orders.filter((item) => item.stage !== "0")) {
        const group = recommendationGroups.find((g) => g.id === order.recommendationGroupId)
        const usedRecommendationIds = group
            ? usedRecommendationIdsByGroup.get(group.id) ?? new Set<string>()
            : new Set<string>()
        const recommendationIds = getRecommendationIdsForOrder(order, group)
            .filter((id) => !usedRecommendationIds.has(id))

        if (!recommendationIds.length) continue

        const matchedRecommendations = (group?.recommendations ?? [])
            .filter((recommendation) => recommendationIds.includes(recommendation.id))

        const comfort = matchedRecommendations.length
            ? Math.max(...matchedRecommendations.map((r) => r.info?.comfort || 0))
            : 0

        measures.push({
            _key: `${strategy.id}:${order.id}`,
            stage: Number(order.stage),
            scenario_applied: order.scenario_applied,
            name: getPlanMeasureName(order, group),
            description: getPlanMeasureDescription(order, group),
            ecmKey: order.ecmKey ?? null,
            routeKey: order.routeKey ?? null,
            durationMonths: order.duration ?? null,
            included: true,
            recommendationGroupId: group?.id ?? null,
            recommendationIds,
            comfort,
            cost: matchedRecommendations.length
                ? formatNumberToString(
                    matchedRecommendations.reduce(
                        (sum, recommendation) => sum + (recommendation.info?.price ?? 0),
                        0
                    )
                )
                : "",
            icon: getIcon(group?.name ?? order.scenarioApplied?.name ?? ""),
            defaultIcon: getDefaultIcon(group?.name ?? order.scenarioApplied?.name ?? "")
        })

        if (group) {
            recommendationIds.forEach((id) => usedRecommendationIds.add(id))
            usedRecommendationIdsByGroup.set(group.id, usedRecommendationIds)
        }
    }

    const displayMeasures = applyRoadmapDisplayRules(strategy, measures, roadmapConfig)
    const byStage = new Map<number, PlanMeasure[]>()

    for (const measure of displayMeasures) {
        const arr = byStage.get(measure.stage) ?? []
        const existingMeasure = measure.recommendationGroupId
            ? arr.find(
                (item) => item.recommendationGroupId === measure.recommendationGroupId
            )
            : undefined

        if (existingMeasure) {
            mergePlanMeasure(existingMeasure, measure)
        } else {
            arr.push(measure)
        }

        byStage.set(measure.stage, arr)
    }

    const stages = [...byStage.keys()].sort((a, b) => a - b)

    return stages.map((stage, idx) => ({
        _key: `${strategy.id}:stage:${stage}`,
        stage,
        year: Math.min(startYear + idx, maxAllowedYear),
        measures: byStage.get(stage) ?? []
    }))
}

function clampInsertIndex(index: number, length: number) {
    return Math.max(0, Math.min(index, length))
}

function clonePlanStepsForValidation<
    TMeasure extends PlanConstraintMeasure,
    TStep extends PlanConstraintStep<TMeasure>
>(steps: TStep[]) {
    return steps.map((step) => ({
        ...step,
        measures: [...step.measures]
    }))
}

function compactPlanStepsForValidation<
    TMeasure extends PlanConstraintMeasure,
    TStep extends PlanConstraintStep<TMeasure>
>(steps: TStep[]) {
    return steps.filter((step) => step.measures.length > 0)
}

function simulateMeasureMove<
    TMeasure extends PlanConstraintMeasure,
    TStep extends PlanConstraintStep<TMeasure>
>(steps: TStep[], payload: PlanMeasureMovePayload) {
    const nextSteps = clonePlanStepsForValidation(steps)
    const sourceStep = nextSteps[payload.sourceStepIndex]
    const targetStep = nextSteps[payload.targetStepIndex]
    const isSameStep = payload.sourceStepIndex === payload.targetStepIndex

    if (!sourceStep || !targetStep) return null
    if (
        payload.sourceMeasureIndex < 0 ||
        payload.sourceMeasureIndex >= sourceStep.measures.length
    ) {
        return null
    }

    const [movedMeasure] = sourceStep.measures.splice(payload.sourceMeasureIndex, 1)
    if (!movedMeasure) return null

    let insertIndex =
        typeof payload.targetMeasureIndex === "number"
            ? payload.targetMeasureIndex
            : targetStep.measures.length

    if (
        isSameStep &&
        typeof payload.targetMeasureIndex === "number" &&
        payload.targetMeasureIndex > payload.sourceMeasureIndex
    ) {
        insertIndex -= 1
    }

    insertIndex = clampInsertIndex(insertIndex, targetStep.measures.length)
    targetStep.measures.splice(insertIndex, 0, movedMeasure)

    return compactPlanStepsForValidation(nextSteps)
}

function simulateStepMove<
    TMeasure extends PlanConstraintMeasure,
    TStep extends PlanConstraintStep<TMeasure>
>(steps: TStep[], payload: PlanStepMovePayload) {
    if (
        payload.sourceStepIndex < 0 ||
        payload.targetStepIndex < 0 ||
        payload.sourceStepIndex >= steps.length ||
        payload.targetStepIndex > steps.length ||
        payload.sourceStepIndex === payload.targetStepIndex
    ) {
        return null
    }

    const reorderedSteps = clonePlanStepsForValidation(steps)
    const [movedStep] = reorderedSteps.splice(payload.sourceStepIndex, 1)
    if (!movedStep) return null

    const insertIndex = clampInsertIndex(payload.targetStepIndex, reorderedSteps.length)
    reorderedSteps.splice(insertIndex, 0, movedStep)

    return compactPlanStepsForValidation(reorderedSteps)
}

function getRouteKeyFromSteps<
    TMeasure extends PlanConstraintMeasure,
    TStep extends PlanConstraintStep<TMeasure>
>(steps: TStep[]) {
    for (const step of steps) {
        for (const measure of step.measures) {
            if (measure.routeKey) return measure.routeKey
        }
    }

    return null
}

function getStepIndexByEcmKey<
    TMeasure extends PlanConstraintMeasure,
    TStep extends PlanConstraintStep<TMeasure>
>(steps: TStep[]) {
    const positions = new Map<BrpEcmKey, number>()

    steps.forEach((step, stepIndex) => {
        step.measures.forEach((measure) => {
            if (measure.ecmKey && !positions.has(measure.ecmKey)) {
                positions.set(measure.ecmKey, stepIndex)
            }
        })
    })

    return positions
}

function isAtOrBefore(
    positions: Map<BrpEcmKey, number>,
    left: BrpEcmKey,
    right: BrpEcmKey
) {
    const leftPos = positions.get(left)
    const rightPos = positions.get(right)

    if (leftPos == null || rightPos == null) return true
    return leftPos <= rightPos
}

function areAllAtOrBefore(
    positions: Map<BrpEcmKey, number>,
    leftKeys: BrpEcmKey[],
    rightKeys: BrpEcmKey[]
) {
    return leftKeys.every((leftKey) =>
        rightKeys.every((rightKey) => isAtOrBefore(positions, leftKey, rightKey))
    )
}

function validateMaxComfortRoute(positions: Map<BrpEcmKey, number>) {
    const middleKeys: BrpEcmKey[] = ["wall_full", "windows_doors", "mech_vent"]
    const endingKeys: BrpEcmKey[] = ["heat_pump", "pv_8"]

    return (
        areAllAtOrBefore(positions, ["roof"], middleKeys) &&
        areAllAtOrBefore(positions, ["roof"], endingKeys) &&
        areAllAtOrBefore(positions, middleKeys, endingKeys)
    )
}

function validateEnergyIndependentsRoute(positions: Map<BrpEcmKey, number>) {
    const fabricKeys: BrpEcmKey[] = ["wall_full", "windows_doors"]
    const finalKeys: BrpEcmKey[] = ["mech_vent", "heat_pump"]

    const pvFirstPattern =
        isAtOrBefore(positions, "pv_8", "roof") &&
        isAtOrBefore(positions, "roof", "wall_cavity") &&
        isAtOrBefore(positions, "wall_cavity", "heating_controls") &&
        areAllAtOrBefore(positions, ["heating_controls"], fabricKeys) &&
        areAllAtOrBefore(positions, fabricKeys, finalKeys)

    const pvAfterClusterPattern =
        isAtOrBefore(positions, "roof", "wall_cavity") &&
        isAtOrBefore(positions, "wall_cavity", "heating_controls") &&
        isAtOrBefore(positions, "heating_controls", "pv_8") &&
        areAllAtOrBefore(positions, ["pv_8"], fabricKeys) &&
        areAllAtOrBefore(positions, fabricKeys, finalKeys)

    return pvFirstPattern || pvAfterClusterPattern
}

function validateRejectFossilFuelsRoute(positions: Map<BrpEcmKey, number>) {
    const pvKey: BrpEcmKey = positions.has("pv_16") ? "pv_16" : "pv_8"
    const fabricKeys: BrpEcmKey[] = ["wall_full", "windows_doors"]
    const endingKeys: BrpEcmKey[] = ["heat_pump", "mech_vent"]

    return (
        areAllAtOrBefore(positions, [pvKey], ["roof"]) &&
        areAllAtOrBefore(positions, ["roof"], fabricKeys) &&
        areAllAtOrBefore(positions, fabricKeys, endingKeys)
    )
}

function validateBoilerSwitchersRoute(positions: Map<BrpEcmKey, number>) {
    const secondBucket: BrpEcmKey[] = ["mech_vent", "pv_8"]
    const fabricKeys: BrpEcmKey[] = ["roof", "wall_full", "windows_doors"]

    return (
        areAllAtOrBefore(positions, ["heat_pump"], secondBucket) &&
        areAllAtOrBefore(positions, secondBucket, fabricKeys)
    )
}

function isValidPlanConfiguration<
    TMeasure extends PlanConstraintMeasure,
    TStep extends PlanConstraintStep<TMeasure>
>(steps: TStep[]) {
    const compactSteps = compactPlanStepsForValidation(steps)
    const routeKey = getRouteKeyFromSteps(compactSteps)
    if (!routeKey) return true

    const positions = getStepIndexByEcmKey(compactSteps)

    switch (routeKey) {
        case "max_comfort":
            return validateMaxComfortRoute(positions)
        case "energy_independents":
            return validateEnergyIndependentsRoute(positions)
        case "reject_fossil_fuels":
            return validateRejectFossilFuelsRoute(positions)
        case "boiler_switchers":
            return validateBoilerSwitchersRoute(positions)
        default:
            return true
    }
}

export function canMovePlanMeasure<
    TMeasure extends PlanConstraintMeasure,
    TStep extends PlanConstraintStep<TMeasure>
>(steps: TStep[], payload: PlanMeasureMovePayload) {
    const nextSteps = simulateMeasureMove(steps, payload)
    if (!nextSteps) return false
    return isValidPlanConfiguration(nextSteps)
}

export function canMovePlanStep<
    TMeasure extends PlanConstraintMeasure,
    TStep extends PlanConstraintStep<TMeasure>
>(steps: TStep[], payload: PlanStepMovePayload) {
    const nextSteps = simulateStepMove(steps, payload)
    if (!nextSteps) return false
    return isValidPlanConfiguration(nextSteps)
}
